/**
 * Validation of URLs used for top-level navigation (window.location.href).
 *
 * The payment flow redirects the buyer to a hosted checkout page whose URL is
 * returned by the Spond backend. The set of possible hostnames is controlled
 * server-side and differs per payment provider and environment (Nets Easy,
 * Vipps MobilePay, the GBR checkout provider, dev1.spond.dev, localhost, ...),
 * so a frontend hostname allowlist could break payments whenever a provider
 * or environment changes. Instead we enforce a strict protocol allowlist,
 * which closes the actual XSS vector (javascript:, data:, vbscript:, blob:
 * and similar scheme-based payloads) without restricting legitimate hosts:
 *
 * - https: is always allowed
 * - http: is only allowed for localhost (local development)
 * - relative paths are allowed and resolve against the current origin
 * - protocol-relative URLs (//host) are rejected; the backend never returns
 *   them, and they would silently inherit the current protocol
 * - everything else is rejected
 */

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];

/**
 * Parses and validates a redirect target. Returns the normalized absolute
 * URL if it is safe to navigate to, or null if it must be rejected.
 */
export const getSafeRedirectUrl = (url: string): string | null => {
    if (typeof url !== "string" || url.trim() === "") {
        return null;
    }

    // Protocol-relative URLs are never produced by our backend and would
    // inherit the current protocol, so reject them outright. The WHATWG URL
    // parser strips tabs and newlines, so do the same before checking.
    const stripped = url.replace(/[\t\n\r]/g, "").trimStart();
    if (stripped.startsWith("//")) {
        return null;
    }

    let parsed: URL;
    try {
        parsed = new URL(url, window.location.origin);
    } catch {
        // Unparseable input can never be a safe redirect target.
        return null;
    }

    if (parsed.protocol === "https:") {
        return parsed.href;
    }

    // Plain http is only used when developing against localhost.
    if (
        parsed.protocol === "http:" &&
        LOCAL_HOSTNAMES.includes(parsed.hostname)
    ) {
        return parsed.href;
    }

    return null;
};

/**
 * Navigates to the given URL if it passes validation. Returns false (after
 * logging) without navigating if the URL is rejected.
 */
export const safeRedirect = (url: string): boolean => {
    const safeUrl = getSafeRedirectUrl(url);
    if (safeUrl === null) {
        console.error("Blocked redirect to unsafe URL:", url);
        return false;
    }
    window.location.href = safeUrl;
    return true;
};
